
import { InspectionDef, LocalInspectionsData, PointValue, InspectionPointImage, getInspectionResultsData } from "../models/inspections/Inspections";
import { get, set } from "./Preferences";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";

const _inspectionsKey = "InspectionWorkOrders";

// User Story 889870: bound the locally stored inspection drafts so they can't
// grow without limit (each abandoned, never-completed inspection leaves a
// draft, and on web these live in ~5MB localStorage). Keep at most
// MAX_LOCAL_INSPECTION_DRAFTS entries and drop anything older than
// DRAFT_TTL_MS. Drafts that are still queued for offline completion are never
// evicted (that would lose data awaiting sync).
const MAX_LOCAL_INSPECTION_DRAFTS = 25;
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 days

const inspectionKey = (
    inspectionId: string,
    workOrder?: string,
    equipment?: string
): string => `${inspectionId}|${workOrder ?? ""}|${equipment ?? ""}`;

const keyOfEntry = (entry: LocalInspectionsData): string =>
    inspectionKey(entry.InspectionId, entry.WorkOrder, entry.Equipment);

// Keys of inspections still queued for offline completion — these must never be
// evicted, since their data is waiting to be synced to the server.
const getProtectedKeys = (): Set<string> => {
    try {
        const pending =
            useWoOfflineUpdatesStore.getState().inspectionToBeCompleted ?? [];
        return new Set(
            pending.map((i) =>
                inspectionKey(i.inspectionId, i.woNumber, i.equipmentId)
            )
        );
    } catch {
        // If the store isn't ready, protect nothing extra rather than throw.
        return new Set<string>();
    }
};

// Apply TTL + LRU limits. keepKey (the just-saved entry) and protected entries
// are always retained regardless of age or count.
const pruneInspections = (
    inspections: LocalInspectionsData[],
    keepKey: string,
    protectedKeys: Set<string>,
    now: number
): LocalInspectionsData[] => {
    const isEvictable = (entry: LocalInspectionsData) => {
        const key = keyOfEntry(entry);
        return key !== keepKey && !protectedKeys.has(key);
    };

    // 1. Drop expired evictable drafts.
    let pruned = inspections.filter((entry) => {
        if (!isEvictable(entry)) return true;
        return now - (entry.SavedAt ?? 0) < DRAFT_TTL_MS;
    });

    // 2. Enforce the count cap by evicting the oldest evictable drafts.
    if (pruned.length > MAX_LOCAL_INSPECTION_DRAFTS) {
        const evictable = pruned
            .filter(isEvictable)
            .sort((a, b) => (a.SavedAt ?? 0) - (b.SavedAt ?? 0));
        const removeCount = pruned.length - MAX_LOCAL_INSPECTION_DRAFTS;
        const removeKeys = new Set(
            evictable.slice(0, removeCount).map(keyOfEntry)
        );
        pruned = pruned.filter((entry) => !removeKeys.has(keyOfEntry(entry)));
    }

    return pruned;
};

const getLocalInspections = async (): Promise<LocalInspectionsData[]> => {
    return (await get(_inspectionsKey)) as LocalInspectionsData[] || [];
};

const setLocalInspections = async (inspectionArray: LocalInspectionsData[]) => {
    return await set(_inspectionsKey, inspectionArray);
}

// Persist with a storage-quota guard: if the write fails (e.g. localStorage
// QuotaExceededError on web), drop the oldest evictable draft and retry so the
// current inspection still gets saved. Returns whether the write succeeded.
const persistInspections = async (
    inspections: LocalInspectionsData[],
    keepKey: string,
    protectedKeys: Set<string>,
    now: number
): Promise<boolean> => {
    let toSave = pruneInspections(inspections, keepKey, protectedKeys, now);

    // Bounded retry loop; each failure sheds one more evictable draft.
    for (let attempt = 0; attempt <= MAX_LOCAL_INSPECTION_DRAFTS; attempt++) {
        try {
            await setLocalInspections(toSave);
            return true;
        } catch (error) {
            const evictable = toSave
                .filter((entry) => {
                    const key = keyOfEntry(entry);
                    return key !== keepKey && !protectedKeys.has(key);
                })
                .sort((a, b) => (a.SavedAt ?? 0) - (b.SavedAt ?? 0));

            if (evictable.length === 0) {
                // Only the current + protected drafts remain and it still won't
                // fit — surface the failure rather than lose those.
                console.error(
                    "Failed to persist inspection draft; local storage is full.",
                    error
                );
                return false;
            }

            const dropKey = keyOfEntry(evictable[0]);
            toSave = toSave.filter((entry) => keyOfEntry(entry) !== dropKey);
        }
    }
    return false;
};

// User Story 889870: purge expired inspection drafts. Safe to call on app
// start; never touches drafts queued for offline completion.
export const pruneLocalInspections = async (): Promise<void> => {
    const inspections = await getLocalInspections();
    if (!inspections.length) return;
    const now = Date.now();
    const protectedKeys = getProtectedKeys();
    const pruned = pruneInspections(inspections, "", protectedKeys, now);
    if (pruned.length !== inspections.length) {
        try {
            await setLocalInspections(pruned);
        } catch (error) {
            console.error("Failed to prune inspection drafts.", error);
        }
    }
};

export const getInspectionFromLocal = async (
    inspectionId: string,
    workOrderId: string,
    equipmentId?: string
): Promise<LocalInspectionsData | null> => {
    const inspections = await getLocalInspections();
    const useEquipment = !workOrderId && !!equipmentId;

    const found = inspections.find((val) => {
        if (useEquipment) {
            return (
                val.InspectionId === inspectionId &&
                val.Equipment === equipmentId
            );
        }
        return (
            val.InspectionId === inspectionId && val.WorkOrder === workOrderId
        );
    });
    return found ?? null;
};

export const saveInspectionToLocal = async (
    woNumber: string, 
    equipmentId: string, 
    inspectionId: string, 
    pointValues: PointValue[], 
    inspectionDef: InspectionDef, 
    failedFlag: boolean, 
    comments?: string, 
    testEquipment?: string,
    pointImages?: { [key: string]: InspectionPointImage[] },
    pointComments?: { [key: string]: string }
) => {
    const enrichedPointValues = pointValues.map(point => {
        if (point.Images !== undefined && point.Comments !== undefined) {
            return point; 
        } else {
            const key = `${point.PointId}_${point.ObjectId}`;
            return {
                ...point,
                Images: pointImages?.[key] || [],
                Comments: pointComments?.[key] || ""
            };
        }
    });

    const isEquipmentContext = !woNumber && !!equipmentId;

    const data = await getInspectionResultsData(
        woNumber, 
        inspectionId, 
        enrichedPointValues, 
        inspectionDef, 
        failedFlag, 
        comments, 
        testEquipment,
        pointImages,
        pointComments,
        equipmentId,
        isEquipmentContext
    );
    
    const localInspections = await getLocalInspections();

    const newInspection: LocalInspectionsData = {
        ActionResult: {
            MessageList: [],
            Success: true,
        },
        Equipment: equipmentId,
        InspectionId: inspectionId,
        InspectionResultData: data,
        Messages: [],
        PrevStatus: "",
        Status: "InProcess",
        WorkOrder: woNumber,
        PointEqpList: {},
        SavedAt: Date.now(),
    };

    const updatedInspections = [
        ...localInspections.filter((insp: LocalInspectionsData) => {
            const useEquipment = !woNumber && !!equipmentId;
            if (useEquipment) {
                return !(
                    insp.InspectionId === inspectionId &&
                    insp.Equipment === equipmentId
                );
            }
            return !(
                insp.WorkOrder === woNumber &&
                insp.InspectionId === inspectionId
            );
        }),
        newInspection,
    ];

    // Persist with TTL/LRU pruning + a quota guard, always keeping this newly
    // saved draft and any offline-completion drafts (User Story 889870).
    const keepKey = inspectionKey(inspectionId, woNumber, equipmentId);
    const saved = await persistInspections(
        updatedInspections,
        keepKey,
        getProtectedKeys(),
        Date.now()
    );
    if (!saved) return false;

    // Confirm the inspection actually landed in local storage.
    const newlySaved = await getInspectionFromLocal(
        inspectionId,
        woNumber,
        equipmentId
    );
    return !!newlySaved;
};

export const removeInspectionFromLocal = async (
    inspectionId: string,
    workOrderId: string,
    equipmentId?: string
): Promise<void> => {
    const allLocalInspections = await getLocalInspections();
    if (allLocalInspections) {
        const useEquipment = !workOrderId && !!equipmentId;
        const remainingInspections = allLocalInspections.filter(
            (insp: LocalInspectionsData) => {
                if (useEquipment) {
                    return !(
                        insp.InspectionId === inspectionId &&
                        insp.Equipment === equipmentId
                    );
                }
                return !(
                    insp.WorkOrder === workOrderId &&
                    insp.InspectionId === inspectionId
                );
            }
        );
        await setLocalInspections(remainingInspections);
    }
};